import { errorResponseSchema, type ErrorResponse } from '@delegation/contracts';

import type { HTTPError } from 'ky';

export class ApiError extends Error {
  readonly statusCode: number;
  readonly responseError: string;
  readonly requestId?: string;
  readonly details?: unknown;
  override readonly cause?: unknown;

  constructor(envelope: ErrorResponse, cause?: unknown) {
    super(Array.isArray(envelope.message) ? envelope.message.join('; ') : envelope.message);
    this.name = 'ApiError';
    this.statusCode = envelope.statusCode;
    this.responseError = envelope.error;
    this.requestId = envelope.requestId;
    this.cause = cause;
  }
}

export class NetworkError extends Error {
  override readonly cause?: unknown;
  constructor(message = 'Network unavailable', cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export async function parseHttpError(httpError: HTTPError): Promise<ApiError> {
  let jsonBody: unknown = undefined;
  let textBody: string | undefined = undefined;
  try {
    jsonBody = await httpError.response.clone().json();
  } catch {
    try {
      textBody = await httpError.response.clone().text();
    } catch {
      // Both readers failed — fall through to httpError.message.
    }
  }

  const parsed = errorResponseSchema.safeParse(jsonBody);
  if (parsed.success) {
    return new ApiError(parsed.data, httpError);
  }

  // The response is NOT our JSON error envelope — a proxy/gateway error page, an
  // opaque body, etc. NEVER surface the raw body or ky's message here: ky embeds
  // the request METHOD + URL in its message, and a gateway body can be HTML or
  // leak internals. Fall back to a generic, status-appropriate message.
  const status = httpError.response.status;
  const fallback: ErrorResponse = {
    statusCode: status,
    error: httpError.response.statusText || 'Error',
    message: genericMessageForStatus(status),
    timestamp: new Date().toISOString(),
  };
  // textBody is intentionally read (to drain the body) but never surfaced.
  void textBody;
  return new ApiError(fallback, httpError);
}

/// A safe, user-facing message for a status when we have no trusted envelope.
/// Deliberately vague — it must never carry backend detail.
function genericMessageForStatus(status: number): string {
  if (status === 400) return 'The request was invalid. Please check your input and try again.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'The requested item could not be found.';
  if (status === 409) return 'That action conflicts with existing data.';
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
  if (status >= 500) return 'Something went wrong on our end. Please try again in a moment.';
  return 'Something went wrong. Please try again.';
}

const GENERIC_FALLBACK = 'Something went wrong. Please try again.';
const TIMEOUT_MESSAGE = 'The request took too long. Please try again.';
const OFFLINE_MESSAGE = 'You appear to be offline. Check your connection and try again.';
const UNREACHABLE_MESSAGE = "Couldn't reach the server. Please try again in a moment.";

function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'TimeoutError';
}

/// A dropped connection / DNS / CORS / abort — fetch rejects with a TypeError
/// (message varies by browser: "Failed to fetch", "NetworkError…", "Load
/// failed") or an AbortError. We DETECT these but never surface their text.
function isNetworkError(error: unknown): boolean {
  if (error instanceof NetworkError) return true;
  if (!(error instanceof Error)) return false;
  if (error.name === 'AbortError' || error.name === 'NetworkError') return true;
  return error.name === 'TypeError' && /fetch|network|load failed/i.test(error.message);
}

/// THE single place any thrown error becomes text shown to a user. Guarantees a
/// safe, friendly message and NEVER leaks a raw error string (which can embed
/// the request URL, a stack, a gateway HTML page, or backend internals):
///   • our own API error   → its already-sanitised envelope message
///   • timeout             → a "took too long" message
///   • offline / unreachable → a connectivity message
///   • anything else       → a generic fallback
/// Use this at EVERY error display site (toasts, inline alerts, upload catches).
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  // The ky beforeError hook attaches the parsed ApiError onto the thrown error.
  const attached = (error as { apiError?: unknown } | null | undefined)?.apiError;
  if (attached instanceof ApiError) return attached.message;
  if (isTimeoutError(error)) return TIMEOUT_MESSAGE;
  if (isNetworkError(error)) {
    return typeof navigator !== 'undefined' && navigator.onLine === false
      ? OFFLINE_MESSAGE
      : UNREACHABLE_MESSAGE;
  }
  return GENERIC_FALLBACK;
}
